import * as React from "react"
import { VariableSizeList } from "react-window"
import { createRef, useContext } from "react"
import { Child } from "./Child"
import { DraggingElement } from "./DraggingElement"
import { DropZoneElement } from "./DropZoneElement"

export const SortableFixedSizeList = React.forwardRef((props, ref) => {
  const { itemSize, ...rest } = props
  return (
    <SortableVariableSizeList ref={ref} itemSize={() => itemSize} {...rest} />
  )
})

export class SortableVariableSizeList extends React.Component {
  dragRef = createRef()
  dropZoneRef = createRef()
  listRef = createRef()

  startClientY = 0
  startDragObjOffsetY = 0
  hoverIndex = null

  autoScroll = "none"
  autoScrollTimer = null

  constructor(props) {
    super(props)

    this.state = {
      dragging: null
    }

    this.onMouseUp = this.onMouseUp.bind(this)
    this.onMouseMove = this.onMouseMove.bind(this)
    this.mouseDown = this.mouseDown.bind(this)
  }

  getAutoScrollWhenDistanceLessThan() {
    return this.props.autoScrollWhenDistanceLessThan || 50
  }

  getAutoScrollSpeed() {
    return this.props.autoScrollSpeed || 50
  }

  componentWillUnmount() {
    document.body.removeEventListener("mouseup", this.onMouseUp)
    document.body.removeEventListener("mousemove", this.onMouseMove)
    this.setAutoScroll("none", 0)
  }

  mouseDown(e, params) {
    const list = this.listRef.current
    if (list === null) return

    this.startClientY = e.clientY

    const top = parseInt((params.style.top || "0").toString(), 10)

    this.startDragObjOffsetY = top - this.getScrollOffsetTop(list)

    document.body.addEventListener("mouseup", this.onMouseUp)
    document.body.addEventListener("mousemove", this.onMouseMove)

    this.setState({
      dragging: params
    })
  }

  onMouseMove(event) {
    this.updateDragElementPositioning(event.clientY)
    this.checkAutoScroll(event.clientY)
  }

  updateDragElementPositioning(mouseY) {
    const dragRef = this.dragRef.current
    if (dragRef === null) return
    if (this.listRef.current === null) return

    const scrollOffsetTop = this.getScrollOffsetTop(this.listRef.current)

    const dY = mouseY - this.startClientY
    const newY = this.startDragObjOffsetY + dY + scrollOffsetTop
    dragRef.style.top = newY + "px"

    const dropRef = this.dropZoneRef.current
    if (dropRef === null) return

    const { offsetTop, index } = this.getHoverDetails(newY)
    this.hoverIndex = index
    dropRef.style.top = offsetTop + "px"
  }

  getHoverDetails(offsetY) {
    let posY = 0

    for (let i = 0; i < this.props.itemCount; i++) {
      const height = this.props.itemSize(i)

      if (offsetY < posY + height / 2) {
        return {
          offsetTop: posY,
          index: i
        }
      }

      posY += height
    }

    return {
      offsetTop: posY,
      index: this.props.itemCount
    }
  }

  getScrollOffsetTop(list) {
    return this.getScrollRef(list).scrollTop
  }

  getScrollRef(list) {
    // @ts-ignore dangerously reach into list internals, so we can get a ref on the scroll element
    return list._outerRef
  }

  checkAutoScroll(mouseY) {
    if (this.listRef.current === null) return

    const list = this.listRef.current
    const scrollRef = this.getScrollRef(list)

    const rect = scrollRef.getBoundingClientRect()
    const listTop = rect.y
    const listBottom = rect.y + rect.height

    const buffer = this.getAutoScrollWhenDistanceLessThan()

    if (mouseY - listTop < buffer) {
      this.setAutoScroll("up", mouseY)
    } else if (listBottom - mouseY < this.getAutoScrollWhenDistanceLessThan()) {
      this.setAutoScroll("down", mouseY)
    } else {
      this.setAutoScroll("none", mouseY)
    }
  }

  setAutoScroll(scroll, mouseY) {
    if (this.autoScrollTimer !== null) {
      clearInterval(this.autoScrollTimer)
      this.autoScrollTimer = null
    }

    this.autoScroll = scroll

    if (scroll === "none") return

    if (this.dragRef.current === null) return
    if (this.listRef.current === null) return

    let delta = this.getAutoScrollSpeed()
    if (scroll === "up") {
      delta = delta * -1
    }

    this.autoScrollTimer = setInterval(e => {
      if (this.listRef.current === null) return

      const offsetTop = this.getScrollOffsetTop(this.listRef.current)
      const newOffsetTop = offsetTop + delta
      this.listRef.current.scrollTo(newOffsetTop)

      this.updateDragElementPositioning(mouseY)
    }, 100)
  }

  onMouseUp() {
    document.body.removeEventListener("mouseup", this.onMouseUp)
    document.body.removeEventListener("mousemove", this.onMouseMove)

    this.setAutoScroll("none", 0)

    if (this.state.dragging === null) return

    const startIndex = this.state.dragging.index

    this.setState({
      dragging: null
    })

    if (this.hoverIndex !== null) {
      let newIndex = this.hoverIndex
      if (newIndex > startIndex) {
        newIndex = Math.max(0, newIndex - 1)
      }

      this.props.onSortOrderChanged({
        originalIndex: startIndex,
        newIndex: newIndex
      })
    }

    this.hoverIndex = null
  }

  renderDropZoneElement() {
    if (this.state.dragging === null) return

    const style = Object.assign({}, this.state.dragging.style, {
      display: "flex",
      flexDirection: "row",
      alignItems: "stretch",
      zIndex: 2,
      background: "white"
    })

    const dropElement = this.props.dropElement || (
      <div
        style={{
          border: "2px dashed #0087F7",
          borderRadius: "3px",
          margin: "2px",
          flex: 1,
          boxSizing: "border-box"
        }}
      />
    )

    return (
      <div ref={this.dropZoneRef} style={style}>
        {dropElement}
      </div>
    )
  }

  renderDraggingElement() {
    if (this.state.dragging === null) return null

    let { style, ...rest } = this.state.dragging
    const Child = this.props.children

    style = Object.assign(
      {},
      style,
      {
        boxShadow: "1px 1px 5px 0px hsla(0, 0%, 0%, 0.31)",
        zIndex: 3
      },
      this.props.draggingElementStyle || {}
    )

    if (!style.backgroundColor) style.backgroundColor = "white"

    return (
      <Child
        ref={this.dragRef}
        {...rest}
        className={this.props.draggingElementClassName}
        style={style}
        onSortMouseDown={e => {}}
      />
    )
  }

  renderInnerElement() {
    const InnerElement = this.props.innerElementType

    return React.forwardRef(({ children, ...rest }, ref) => {
      const inner = (
        <React.Fragment>
          {children}
          {this.renderDraggingElement()}
          {this.renderDropZoneElement()}
        </React.Fragment>
      )

      if (InnerElement) {
        return (
          <InnerElement {...rest} ref={ref}>
            {inner}
          </InnerElement>
        )
      }

      return (
        <div {...rest} ref={ref}>
          {inner}
        </div>
      )
    })
  }

  sortableContext = {
    Child: this.props.children,
    itemKey: this.props.itemKey,
    onMouseDown: this.mouseDown.bind(this)
  }

  getSortableContext() {
    const value = {
      Child: this.props.children,
      itemKey: this.props.itemKey,
      onMouseDown: this.mouseDown
    }

    if (value.Child === this.sortableContext.Child) {
      if (value.itemKey === this.sortableContext.itemKey) {
        return this.sortableContext
      }
    }

    this.sortableContext = value
    return this.sortableContext
  }

  dragContext = null

  getDragContext() {
    if (!this.state.dragging) return null

    var value = {
      dragging: this.state.dragging,
      dragRef: this.dragRef,
      dropZoneRef: this.dropZoneRef,
      Child: this.props.children
    }

    if (
      this.dragContext === null ||
      this.dragContext.dragging !== value.dragging ||
      this.dragContext.Child !== value.Child
    ) {
      this.dragContext = value
    }

    return this.dragContext
  }

  render() {
    const { children, innerElementType, ...props } = this.props

    return (
      <SortableContext.Provider value={this.getSortableContext()}>
        <InnerElementContext.Provider value={this.props.innerElementType}>
          <DragContext.Provider value={this.getDragContext()}>
            <VariableSizeList
              ref={this.listRef}
              innerElementType={InnerElementType}
              {...props}
            >
              {Child}
            </VariableSizeList>
          </DragContext.Provider>
        </InnerElementContext.Provider>
      </SortableContext.Provider>
    )
  }
}

export const SortableContext = React.createContext({
  Child: () => <div />,
  onMouseDown: () => {},
  itemKey: undefined
})

export const DragContext = React.createContext(null)

const InnerElementContext = React.createContext(undefined)

function InnerElementType(props) {
  const { children, ...rest } = props
  const InnerElement = useContext(InnerElementContext)

  const inner = (
    <React.Fragment>
      {children}
      <DraggingElement />
      <DropZoneElement />
    </React.Fragment>
  )

  if (InnerElement) {
    return <InnerElement {...rest}>{inner}</InnerElement>
  }

  return <div {...rest}>{inner}</div>
}
